import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  Signal,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import Chart, { ChartConfiguration, ChartConfigurationCustomTypesPerDataset } from 'chart.js/auto';
import 'chartjs-adapter-dayjs-4';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { getMinMaxDate } from '../../services/assessmentsHistory.functions';
import { AssessmentChartDataSets, AssessmentType, Patient, assessmentTypeColors } from '../../services/model';

@Component({
  selector: 'app-line-chart-percent',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './line-chart-percent.component.html',
  styleUrl: './line-chart-percent.component.scss',
})
export class LineChartPercentComponent implements OnInit {
  @Input() data!: Signal<AssessmentChartDataSets | undefined>;
  @Input() chartId!: string;
  @Input() type!: 'bar' | 'line';
  @Input() patient!: Patient;
  @ViewChild('canvas', { read: ElementRef, static: true }) canvas!: ElementRef<any>;

  selectedSide: 'left' | 'right' | 'both' = 'left';

  public chart: any;
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    dayjs.locale('de');

    // getPersistedConfig()

    effect(() => {
      this.data();
      if (this.chart) {
        this.createChart(this.selectedSide);
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    this.selectedSide = this.patient.affectedSide === 'l' ? 'left' : 'right';

    setTimeout(() => {
      this.createChart(this.selectedSide);
      this.cdr.detectChanges();
    }, 50);
  }

  onSeiteDropDownSelect(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value as 'left' | 'right' | 'both';
    console.log('Selected value:', selectedValue);
    console.log('selectedSide:', this.selectedSide);
    this.createChart(selectedValue);
  }

  createChart(side: 'left' | 'right' | 'both') {
    let sideScoreKey: 'score_left' | 'score_right' | 'score' = 'score';
    let pointStyle: 'triangle' | 'rect' | 'circle' = 'circle';
    switch (side) {
      case 'left':
        sideScoreKey = 'score_left';
        pointStyle = 'triangle';
        break;
      case 'right':
        sideScoreKey = 'score_right';
        pointStyle = 'rect';
    }

    const datasetsObject: AssessmentChartDataSets = this.data() ?? {};
    const keys: Array<AssessmentType> = Object.keys(datasetsObject) as Array<AssessmentType>;
    const keysSorted: Array<AssessmentType> = keys.sort();
    const datasets = keysSorted.map((type: AssessmentType) => {
      return {
        label: type,
        data: datasetsObject[type]?.map(row => {
          // const score = row[sideScoreKey] as number;
          // Invertierung Schädigung zu Erfüllung
          const score = 100 - (row[sideScoreKey] as number);
          return { x: new Date(new Date(row.dateIso).toDateString()), y: score };
        }),

        borderWidth: 1,
        tension: 0.1,
        backgroundColor: assessmentTypeColors[type],
        borderColor: assessmentTypeColors[type],

        pointStyle: pointStyle,
        pointRadius: pointStyle === 'circle' ? 3 : 5,
        pointHoverRadius: 10,
      };
    });

    // const labelDataFull: Array<Date> = keys.flatMap((type: AssessmentType) => {
    //   return datasetsObject[type]!.map(row => new Date(new Date(row.dateIso).toDateString()));
    // });
    // labelDataFull.sort((a, b) => a.getTime() - b.getTime());
    // const maxDate = dayjs(labelDataFull[labelDataFull.length - 1])
    //   .add(3, 'day')
    //   .toISOString();
    // const minDate = dayjs(labelDataFull[0]).subtract(3, 'day').toISOString();

    const { minDate, maxDate, labelDataFull } = getMinMaxDate(datasetsObject);

    const config = this.getChartConfig(datasets, minDate, maxDate, labelDataFull);

    if (this.chart) {
      this.chart.data = { labels: labelDataFull, datasets: datasets };
      this.chart.options.scales.x.min = minDate;
      this.chart.options.scales.x.max = maxDate;
      this.chart.update();
    } else {
      this.chart = new Chart(this.canvas.nativeElement, config);
    }
  }

  getChartConfig(
    datasets: any,
    minDate: any,
    maxDate: any,
    labelDataFull: any
  ): ChartConfiguration | ChartConfigurationCustomTypesPerDataset {
    return {
      type: 'line',
      data: {
        labels: labelDataFull,
        datasets: datasets,
      },
      options: {
        responsive: true,
        aspectRatio: 3,
        animation: {
          delay: 100,
        },
        clip: false,
        layout: {
          // autoPadding: true,
          //   padding: {
          //     left: -10,
          //     right: 10,
          //   },
        },
        scales: {
          y: {
            min: 0,
            max: 100,
            reverse: false,
            position: 'right',
            beginAtZero: true,
            grid: {
              display: false,
            },
            ticks: {
              callback: function (val, index) {
                return val + ' %';
              },
            },
          },
          x: {
            offset: true,
            grid: {
              display: true,
            },
            type: 'time',
            min: minDate,
            max: maxDate,
            time: {
              unit: 'week',
              isoWeekday: true,
              displayFormats: {
                week: 'dd, DD.MM.YY',
              },
              tooltipFormat: 'dd, DD.MM.YYYY',
            },
          },
        },
        plugins: {
          title: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || '';

                if (label) {
                  label += ': ' + context.parsed.y + ' %';
                }
                return label;
              },
            },
          },
          legend: {
            position: 'top',
            align: 'start',
          },
        },
      },
      plugins: [this.customCanvasBackgroundColor], // this.customLegend
    };
  }

  // nach Schädigung (schlechteste Werte oben)
  drawChartBackground(gradientBack: any) {
    gradientBack.addColorStop(0, 'rgba(255,255,255,1)');
    gradientBack.addColorStop(0.04, 'rgba(255,255,255,1)');

    gradientBack.addColorStop(0.04, 'rgba(252,252,239,1)');
    gradientBack.addColorStop(0.25, 'rgba(252,252,239,1)');

    gradientBack.addColorStop(0.25, 'rgba(252,244,219,1)');
    gradientBack.addColorStop(0.49, 'rgba(252,244,219,1)');

    gradientBack.addColorStop(0.49, 'rgba(255,228,200,1)');
    gradientBack.addColorStop(0.95, 'rgba(255,228,200,1)');

    gradientBack.addColorStop(0.95, 'rgba(251,221,221,1)');
    gradientBack.addColorStop(1, 'rgba(251,221,221,1)');
  }

  // nach Erfüllung (beste werte oben)
  drawChartBackgroundReverse(gradientBack: any) {
    gradientBack.addColorStop(0, 'rgba(251,221,221,1)');
    gradientBack.addColorStop(0.05, 'rgba(251,221,221,1)');

    gradientBack.addColorStop(0.05, 'rgba(255,228,200,1)');
    gradientBack.addColorStop(0.51, 'rgba(255,228,200,1)');

    gradientBack.addColorStop(0.51, 'rgba(252,244,219,1)');
    gradientBack.addColorStop(0.75, 'rgba(252,244,219,1)');

    gradientBack.addColorStop(0.75, 'rgba(252,252,239,1)');
    gradientBack.addColorStop(0.96, 'rgba(252,252,239,1)');

    gradientBack.addColorStop(0.96, 'rgba(255,255,255,1)');
    gradientBack.addColorStop(1, 'rgba(255,255,255,1)');
  }

  customCanvasBackgroundColor = {
    id: 'customCanvasBackgroundColor',
    beforeDraw: (chart: any, args: any, options: any) => {
      const {
        ctx,
        chartArea: { left, right, top, bottom, width, height },
      } = chart;
      const canvas = chart.canvas;
      const chartArea = chart.chartArea;

      ctx.save();
      // Chart background
      const gradientBack = canvas.getContext('2d').createLinearGradient(right, bottom, right, top);
      this.drawChartBackgroundReverse(gradientBack);
      ctx.fillStyle = gradientBack;
      ctx.fillRect(
        chartArea.left,
        chartArea.bottom,
        chartArea.right - chartArea.left,
        chartArea.top - chartArea.bottom
      );
    },
  };
}
